import { ADMIN_RUNTIME_COMMANDS, createAdminRuntime } from './adminRuntime';
import { assertRuntimeSaveSucceeded, normalizeRuntimeSaveOutcome } from './saveOutcome';
import { CLOUD_ANALYTICS_REPORTS, normalizeAnalyticsReport } from './runtimeAnalytics';
import { prepareRecentAnalyticsDateRange } from '../../util/appUtils';

const SETTINGS_ROOT = shopId => (
  `/system/v2/shop/${encodeURIComponent(shopId)}/settings`
);

function requireWebserviceClient(client) {
  if (!client || typeof client.prepareRequest !== 'function') {
    throw new Error('Cloud webservice client is not available. Sign in and try again.');
  }
  return client;
}

function assertConfig(config) {
  if (!config || typeof config !== 'object') {
    throw new Error('Cloud preview settings are not available.');
  }
  return config;
}

export function createCloudAdminRuntime({ webserviceClient }) {
  const client = requireWebserviceClient(webserviceClient);

  const load = ({ shopId, mode = 'preview' }) => client.prepareRequest(
    'GET',
    `${SETTINGS_ROOT(shopId)}/search-experience`,
    { qs: { mode } },
  );

  const save = async ({ shopId, config, mode = 'preview' }) => {
    const response = await client.prepareRequest(
      'PUT',
      `${SETTINGS_ROOT(shopId)}/search-experience`,
      { qs: { mode }, body: config },
    );
    return normalizeRuntimeSaveOutcome(response, config);
  };

  const copyToProduction = async (shopId) => {
    const result = await client.prepareRequest(
      'POST',
      `${SETTINGS_ROOT(shopId)}/search-experience/publish`,
    );
    if (result?.success === false || (Array.isArray(result?.errors) && result.errors.length > 0)) {
      const first = result.errors?.[0];
      throw new Error(first?.message || first?.error || 'Production settings validation failed.');
    }
    return assertConfig(result?.config);
  };

  const readOperatorState = async ({ shopId }) => {
    const [indexingStatus, deploymentStatus] = await Promise.all([
      client.prepareRequest(
        'GET',
        `/system/shop/${encodeURIComponent(shopId)}/task/crawler/status`,
      ),
      client.prepareRequest(
        'GET',
        `${SETTINGS_ROOT(shopId)}/search-experience/status`,
      ),
    ]);
    const current = indexingStatus?.current || null;
    const lastSuccessAt = indexingStatus?.lastFull?.successAt
      || indexingStatus?.lastSuccessAt
      || null;
    const hasSearchableCatalog = Boolean(lastSuccessAt);
    const rawStatus = String(
      current?.status || (hasSearchableCatalog ? 'completed' : 'idle'),
    ).toLowerCase();
    const previewBuilt = deploymentStatus?.previewArtifactVerified === true;

    // The shell consumes one product-neutral operator-state shape. Cloud owns
    // this projection here; plugin bridge details never leak into Home.
    return {
      result: 'success',
      state: {
        connection: { connected: true, details: { has_token: true } },
        indexing: {
          status: rawStatus,
          progress: typeof current?.progress === 'number' ? current.progress : 0,
          active_index_present: hasSearchableCatalog,
          started_at: current?.startedAt || current?.started_at || null,
          finished_at: current?.finishedAt || current?.finished_at || null,
          last_success_at: lastSuccessAt,
          error: current?.error || indexingStatus?.lastFull?.errorMessage || null,
          job_id: current?.taskId || current?.jobId || current?.job_id || null,
          indexed_count: current?.indexedCount || current?.indexed_count || 0,
          total_count: current?.totalCount || current?.total_count || 0,
        },
        readiness: { can_search: hasSearchableCatalog },
        onboarding: {
          wizard_completed: deploymentStatus?.wizardCompleted === true,
          preview_built: previewBuilt,
        },
        publishState: deploymentStatus?.state || null,
        capabilities: {},
        context: { platform: deploymentStatus?.platform || 'cloud', shop_id: shopId },
      },
    };
  };

  const handlers = {
    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_LOAD]: load,

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_LOAD_DEFAULTS]: ({
      shopId,
      experience,
      mode = 'preview',
    }) => {
      if (!experience) throw new Error('experience is required');
      return client.prepareRequest(
        'GET',
        `${SETTINGS_ROOT(shopId)}/search-experience/defaults`,
        { qs: { mode, experience } },
      );
    },

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_VALIDATE]: ({ shopId, config }) => client.prepareRequest(
      'POST',
      `${SETTINGS_ROOT(shopId)}/search-experience/validate`,
      { body: config },
    ),

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_SAVE_DRAFT]: save,

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_DESCRIBE]: ({ shopId }) => client.prepareRequest(
      'GET',
      `${SETTINGS_ROOT(shopId)}/search-experience/describe`,
    ),

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_COMPONENTS]: ({ shopId }) => client.prepareRequest(
      'GET',
      `${SETTINGS_ROOT(shopId)}/components`,
    ),

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_DEPLOYMENT_STATUS]: ({ shopId }) => client.prepareRequest(
      'GET',
      `${SETTINGS_ROOT(shopId)}/search-experience/status`,
    ),

    [ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_COPY_TO_PRODUCTION]: async ({ shopId }) => ({
      config: await copyToProduction(shopId),
    }),

    // Cloud persistence already owns draft state. These semantic lifecycle
    // commands are no-ops here and bridge side effects in the plugin adapter.
    [ADMIN_RUNTIME_COMMANDS.SETTINGS_PREVIEW_SAVED]: async () => ({ success: true }),
    [ADMIN_RUNTIME_COMMANDS.ONBOARDING_MODE_SELECTED]: async () => ({ success: true }),
    [ADMIN_RUNTIME_COMMANDS.ONBOARDING_INTEGRATION_CONFIGURED]: async () => ({ success: true }),

    [ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_PREVIEW]: async ({ shopId }) => {
      const details = await client.prepareRequest(
        'GET',
        `/system/shop/${encodeURIComponent(shopId)}/bundle/build-preview`,
      );
      return {
        success: true,
        status: 'success',
        operation: 'preview',
        details,
      };
    },

    [ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_AND_PROMOTE]: async ({ shopId }) => {
      const result = await client.prepareRequest(
        'POST',
        `${SETTINGS_ROOT(shopId)}/search-experience/deploy`,
      );
      if (result?.success === false || result?.status === 'failed') {
        throw new Error(result?.error || 'Search interface publishing failed.');
      }
      return {
        ...result,
        success: true,
        status: 'success',
        operation: 'production',
      };
    },

    [ADMIN_RUNTIME_COMMANDS.CRAWLER_CONNECTIVITY_CHECK]: ({ shopId, settings }) => (
      client.prepareRequest('POST', '/v2/public/plugin/check-connectivity', {
        body: {
          shopId,
          baseUrl: settings.baseUrl,
          authType: settings.authType,
          authLogin: settings.authLogin,
          authPassword: settings.authPassword,
        },
      })
    ),

    [ADMIN_RUNTIME_COMMANDS.CRAWLER_SETTINGS_SAVE]: async ({ shopId, settings }) => {
      const response = await client.prepareRequest(
        'POST',
        `/system/shop/${encodeURIComponent(shopId)}/settings`,
        { body: { crawler: settings } },
      );
      assertRuntimeSaveSucceeded(response, 'Crawler settings were not saved.');
      return { success: true, settings, details: response };
    },

    [ADMIN_RUNTIME_COMMANDS.ANALYTICS_REPORT]: async ({
      shopId,
      reportType,
      limit = 50,
      from = null,
      to = null,
      timeframeDays = null,
    }) => {
      const report = CLOUD_ANALYTICS_REPORTS[reportType];
      if (!report) throw new Error(`Analytics report "${reportType}" is not available in cloud.`);
      const range = (!from && !to && timeframeDays)
        ? prepareRecentAnalyticsDateRange(timeframeDays)
        : { from, to };
      const data = await client.prepareRequest('GET', report.path, {
        qs: {
          token: shopId,
          from: range.from,
          to: range.to,
          fields: 'records+users+queries+curve',
          limit,
          ...report.query,
        },
      });
      return normalizeAnalyticsReport(data, reportType);
    },

    [ADMIN_RUNTIME_COMMANDS.OPERATOR_STATE]: readOperatorState,

    [ADMIN_RUNTIME_COMMANDS.INDEXING_STATUS]: ({ shopId }) => client.prepareRequest(
      'GET',
      `/system/shop/${encodeURIComponent(shopId)}/task/crawler/status`,
    ),

    [ADMIN_RUNTIME_COMMANDS.INDEXING_HISTORY]: ({ shopId }) => client.prepareRequest(
      'GET',
      `/system/shop/${encodeURIComponent(shopId)}/task/crawler/history`,
    ),

    [ADMIN_RUNTIME_COMMANDS.INDEXING_START]: ({ shopId }) => client.prepareRequest(
      'POST',
      `/system/shop/${encodeURIComponent(shopId)}/index/rebuild`,
      { qs: { background: true, full: true, tracked: true } },
    ),

    [ADMIN_RUNTIME_COMMANDS.PREVIEW_RESOLVE_URL]: ({ url, timeout = 20000 }) => (
      client.prepareRequest('POST', '/v2/public/plugin/resolve-redirects', {
        body: { url, timeout },
      })
    ),
  };

  return createAdminRuntime({ productId: 'cloud', handlers });
}
